/**
 * New Year worksheets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearWorksheetsPack: ToilePack = {
    slug: 'new-year-worksheets',
    category: 'season',
    name: 'New Year worksheets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'goals', label: 'My goals', subject: 'A worksheet lettered "My Goals This Year" with three numbered boxes and a star border'},
        {key: 'review', label: 'Year in review', subject: 'A worksheet lettered "My Year" with boxes headed "Best day", "I learned" and "I made" and blank lines'},
        {key: 'writing', label: 'Writing prompt', subject: 'A worksheet lettered "This year I will" with a large ruled panel and a drawing box, a confetti border'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a dark page with a gold border and a margin'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a vertical page with a confetti border and a white margin'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a clean page with wide margins'},
    ],
    directions: ['for a whole-class bulletin board', 'with emoji mood check boxes', 'as a one-page fold-out'],
    directionHint: 'try: emoji mood check boxes',
    seo: {
        title: 'New Year Worksheets: Goals, Review & Writing Prompt',
        metaDescription: 'Nine New Year worksheets — a goals page, a year-in-review page, and a writing prompt — for kids, in black-and-gold, confetti, and minimal styles.',
        h1: 'New Year worksheets: goals, year in review, writing prompt',
        tagline: 'Nine pages, three activities, three styles each — blank where the pupil writes.',
        intro: 'Three New Year worksheets for January: a goals page, a year-in-review page, and a writing prompt about the year ahead, each in black-and-gold, confetti-party, and minimal styles. Every header prints exactly as lettered; every line stays blank for a pencil. Ask for an older-kid version, cursive lines, or a two-page spread, and the same worksheet grammar carries it.',
        useCases: [
            'a classroom January activity',
            'a homeschool new-year unit',
            'a family goal-setting evening',
            'a Sunday school lesson',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each worksheet is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Are these fillable digital forms, or pages I print and write on?', a: "They're printed pages: the lines and boxes are left blank in the image for a pencil, not editable text fields."},
            {q: 'Can I get an older-kid version or cursive lines?', a: 'Yes — the direction field takes any variation, including an older-kid layout or cursive lines.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
